import React from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  PhoneCall,
  MapPin,
  Users,
  MessagesSquare,
  Timer,
  CheckCircle2,
  Building2,
  School2,
  Bus,
  ShieldCheck,
  Star,
} from "lucide-react";

// If you have a real asset, replace this with: import congralation from "../../assets/inter/congralations.webp";
const congralation =
  "https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?q=80&w=1600&auto=format&fit=crop";

/***************************
 * DATA (Sample — replace with yours)
 ***************************/
export const sampleBadges = [
  { icon: <Star className="h-3.5 w-3.5" />, text: "Top Results" },
  { icon: <ShieldCheck className="h-3.5 w-3.5" />, text: "Trusted by Parents" },
  { icon: <Building2 className="h-3.5 w-3.5" />, text: "Modern Campus" },
];

export const sampleFeatures = [
  {
    icon: <School2 className="h-5 w-5" />,
    title: "India’s Top Faculty – Mentors Who Care",
    bullets: [
      "Experienced mentors from IITs, NITs, AIIMS",
      "Concept → Application → Exam mastery",
      "One-on-one doubt clearing & guidance",
    ],
  },
  {
    icon: <CheckCircle2 className="h-5 w-5" />,
    title: "A Legacy of Results",
    bullets: [
      "Top ranks in IIT‑JEE & NEET",
      "Admissions to IITs, AIIMS, NITs",
      "Transparent, data‑driven progress",
    ],
  },
  {
    icon: <LineChartIcon />,
    title: "Continuous Assessment & Feedback",
    bullets: [
      "Daily/Weekly/Monthly exams",
      "Mentor reviews & parent reports",
      "Personalized improvement plans",
    ],
  },
];

export const samplePrograms = [
  {
    icon: <ShieldCheck className="h-3.5 w-3.5" />,
    tag: "Residential",
    points: [
      "24×7 supervised hostels",
      "Morning & evening doubt sessions",
      "Wholesome food + study discipline",
    ],
  },
  {
    icon: <Bus className="h-3.5 w-3.5" />,
    tag: "Semi‑Residential",
    points: [
      "Day coaching + evening study halls",
      "Transport available",
      "Mentor tracking & reports",
    ],
  },
  {
    icon: <Building2 className="h-3.5 w-3.5" />,
    tag: "Day Scholar",
    points: [
      "Balanced schedule",
      "IIT‑JEE/NEET integrated with Boards",
      "After‑school doubt clearing",
    ],
  },
];

export const sampleParentsLove = [
  "Caring mentors & small batches",
  "Regular updates and honest feedback",
  "Clean hostels, good food, and safety",
  "Improved confidence and discipline",
];

/***************************
 * UTIL (Tiny icon to avoid importing from lucide-react LineChart with same name)
 ***************************/
function LineChartIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M3 3v18h18" />
      <path d="M19 7l-5 5-4-2-4 5" />
    </svg>
  );
}

/***************************
 * 1) HERO SECTION
 ***************************/
export function HeroSection({ badges = sampleBadges, sparktext='',description='',title='', courseName1='', courseName2='',titleend='',symbol='', imagePath='' }) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-100 via-teal-100 to-sky-100" />
      <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-tr from-teal-300/30 to-emerald-300/30 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-tr from-sky-300/30 to-teal-300/30 blur-3xl" />

      <div className="mx-auto max-w-7xl px-1 py-2 lg:flex lg:items-center lg:gap-1 lg:py-2">
        {/* Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl text-center lg:mx-0 lg:text-left"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-teal-300 bg-white/70 px-3 py-1 text-xs font-semibold text-teal-800 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> {sparktext}
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
           {title} <span className="text-teal-700">{courseName1}</span> {symbol+" "}
            <span className="text-emerald-700">{courseName2}</span> {titleend}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-neutral-700">
          {description}
          </p>

          {/* Badges */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {badges.map((b, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-neutral-700 shadow-sm ring-1 ring-black/5"
              >
                {b.icon}
                {b.text}
              </span>
            ))}
          </div>

          {/* CTA buttons */}
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:items-start">
            <a
              href="tel:+919959271353"
              className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold shadow-sm ring-1 ring-black/10 bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600/40"
            >
              <PhoneCall className="mr-2 h-4 w-4" /> Call: +91 99 59 271 353
            </a>
            <a
              href="https://www.rankridge.com/contact-us/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold bg-white text-teal-700 ring-1 ring-black/10 hover:bg-teal-50"
            >
              <MapPin className="mr-2 h-4 w-4" /> Enquiry For Admissions
            </a>
          </div>
        </motion.div>

        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12 lg:mt-0 lg:flex-1"
        >
          <div className="relative mx-auto w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-black/5">
            <div className="aspect-[16/11] bg-gradient-to-br from-white to-teal-50" />
            <div className="absolute inset-0 grid place-items-center">
              <img
                src={imagePath}
                className="w-full rounded-2xl object-contain h-auto"
                loading="lazy"
                alt="Rankridge Celebration"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/***************************
 * 2) ABOUT SECTION
 ***************************/
export function AboutSection({title='', description='', description2='', imagePath=''}) {
  return (
    <section className="mx-auto max-w-7xl px-6 py-1 lg:py-2">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <img src={imagePath} className="rounded-2xl h-auto" />
          
        </div>

        <div>
          <h2 className="text-3xl font-bold text-center tracking-tight sm:text-4xl">{title}</h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
           {description}
               </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
           {description2}
              </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-neutral-700">
            <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 ring-1 ring-teal-200 text-teal-700">
              <Users className="h-4 w-4" /> Small Batches
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 ring-1 ring-emerald-200 text-emerald-700">
              <MessagesSquare className="h-4 w-4" /> Mentor Feedback
            </span>
            <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 ring-1 ring-sky-200 text-sky-700">
              <Timer className="h-4 w-4" /> Consistent Assessments
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/***************************
 * 3) WHY TRUST SECTION
 ***************************/
export function WhyTrustSection({ features = sampleFeatures }) {
  return (
    <section className="mx-auto max-w-7xl px-6 py-1 lg:py-2 bg-[#20b2aa]">
      <div className="mx-auto max-w-3xl text-center">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mt-4">Why Parents Across Telangana Trust Rankridge</h2>
        <p className="mt-3 text-black">
          Designed for dual success—Boards + IIT‑JEE/NEET—backed by mentors, measurable progress, and a caring system.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            className="group h-full rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5 hover:shadow-md"
          >
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-teal-50 text-teal-700 ring-1 ring-teal-200">
                {f.icon}
              </div>
              <h3 className="text-lg font-semibold leading-tight">{f.title}</h3>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-neutral-700">
              {f.bullets.map((b, j) => (
                <li key={j} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/***************************
 * 4) PROGRAMS SECTION
 ***************************/
export function ProgramsSection({ programs = samplePrograms }) {
  return (
    <section className="relative bg-gradient-to-b from-white to-teal-50/60 py-1 ">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mt-4">Flexible Learning Modes</h2>
          <p className="mt-3 text-neutral-600">Choose a program that fits your child’s needs.</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {programs.map((p, i) => (
            <div key={i} className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5">
              <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 ring-1 ring-teal-200">
                {p.icon}
                {p.tag}
              </span>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                {p.points.map((pt, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/***************************
 * 5) PARENTS LOVE SECTION
 ***************************/
export function ParentsLoveSection({ parentsLove = sampleParentsLove }) {
  return (
    <section className="mx-auto max-w-7xl px-6 py-1">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">What Parents Love About Rankridge</h2>
          <ul className="mt-6 space-y-3 text-neutral-700">
            {parentsLove.map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-base">
                <CheckCircle2 className="mt-1 h-5 w-5 flex-none text-emerald-600" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Visual card */}
        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <div className="aspect-[16/10] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50" />
          <div className="mt-4 text-sm text-neutral-600">
            Placeholder for parent testimonial photo / campus image. Replace with an actual image.
          </div>
        </div>
      </div>
    </section>
  );
}

/***************************
 * 6) CTA SECTION
 ***************************/
export function CTASection() {
  return (
    <section className="relative isolate mx-4 overflow-hidden rounded-3xl bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 px-6 py-12 text-white shadow-xl sm:mx-auto sm:max-w-7xl sm:px-12">
      <div className="mx-auto max-w-3xl text-center">
        <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">Admissions Open – Limited Seats for 2025</h3>
        <p className="mt-2 text-white/90">
          Join Hyderabad’s premier Intermediate college for integrated IIT‑JEE & NEET coaching. Give your child the
          Rankridge edge.
        </p>
        <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="tel:+919959271353"
            className="inline-flex items-center justify-center rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-teal-700 ring-1 ring-white/20 hover:bg-white/95"
          >
            <PhoneCall className="mr-2 h-4 w-4" /> Call: +91 99 59 271 353
          </a>
          <a
            href="https://www.rankridge.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center rounded-2xl bg-white/10 px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/15"
          >
            <MapPin className="mr-2 h-4 w-4" /> Visit Website
          </a>
        </div>
      </div>
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
    </section>
  );
}

